import { getPosSettings } from "./posSettings";

let audioCtx: AudioContext | null = null;

export const resetAudioContext = (): void => {
  audioCtx = null;
};

const getAudioContext = (): AudioContext | null => {
  if (typeof window === "undefined") return null;
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return null;
    if (!audioCtx) {
      audioCtx = new AudioContextClass();
    }
    if (audioCtx.state === "suspended") {
      audioCtx.resume().catch(() => {});
    }
    return audioCtx;
  } catch {
    return null;
  }
};

/**
 * Plays a cheerful 3-tone chime (C5 -> E5 -> G5) to announce incoming orders.
 */
export const playNewOrderChime = (overrideEnabled?: boolean): void => {
  const isEnabled = overrideEnabled !== undefined ? overrideEnabled : getPosSettings().soundAlertsEnabled;
  if (!isEnabled) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime || 0;
    const notes = [
      { freq: 523.25, start: 0, duration: 0.12 },    // C5
      { freq: 659.25, start: 0.14, duration: 0.12 }, // E5
      { freq: 783.99, start: 0.28, duration: 0.35 }, // G5
    ];

    notes.forEach(({ freq, start, duration }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      if (!osc || !gain) return;

      osc.type = "sine";
      osc.frequency?.setValueAtTime?.(freq, now + start);

      gain.gain?.setValueAtTime?.(0, now + start);
      gain.gain?.linearRampToValueAtTime?.(0.25, now + start + 0.02);
      gain.gain?.exponentialRampToValueAtTime?.(0.001, now + start + duration);

      osc.connect?.(gain);
      if (ctx.destination) {
        gain.connect?.(ctx.destination);
      }

      osc.start?.(now + start);
      osc.stop?.(now + start + duration);
    });
  } catch (err) {
    console.warn("Failed to play new order chime:", err);
  }
};

/**
 * Plays a subtle 2-tone ping (A5 -> D6) for incoming chat messages.
 */
export const playNewMessageChime = (overrideEnabled?: boolean): void => {
  const isEnabled = overrideEnabled !== undefined ? overrideEnabled : getPosSettings().soundAlertsEnabled;
  if (!isEnabled) return;

  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const now = ctx.currentTime || 0;
    const notes = [
      { freq: 880.0, start: 0, duration: 0.08 },      // A5
      { freq: 1174.66, start: 0.1, duration: 0.25 },  // D6
    ];

    notes.forEach(({ freq, start, duration }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      if (!osc || !gain) return;

      osc.type = "triangle";
      osc.frequency?.setValueAtTime?.(freq, now + start);

      gain.gain?.setValueAtTime?.(0, now + start);
      gain.gain?.linearRampToValueAtTime?.(0.18, now + start + 0.02);
      gain.gain?.exponentialRampToValueAtTime?.(0.001, now + start + duration);

      osc.connect?.(gain);
      if (ctx.destination) {
        gain.connect?.(ctx.destination);
      }

      osc.start?.(now + start);
      osc.stop?.(now + start + duration);
    });
  } catch (err) {
    console.warn("Failed to play message chime:", err);
  }
};

